// src/components/common/FileUpload.jsx - FIXED VERSION
import React, { useState, useRef } from 'react';
import { Upload, X, File, Image, Loader2, AlertCircle } from 'lucide-react';

export const FileUpload = ({ 
  onFileSelect, 
  currentFile, 
  onRemove, 
  accept = "image/*,.pdf,.txt",
  maxSize = 10,
  label = "Upload File",
  uploading = false,
  error = null,
  showPreview = true,
  multiple = false
}) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;

    if (multiple) {
      // Handle multiple files
      const fileArray = Array.from(files);
      const maxSizeBytes = maxSize * 1024 * 1024;
      const oversizedFiles = fileArray.filter(file => file.size > maxSizeBytes);
      
      if (oversizedFiles.length > 0) {
        onFileSelect(null, `Some files exceed ${maxSize}MB limit`);
        return;
      }

      onFileSelect(fileArray);
    } else {
      // Handle single file
      const file = files[0] || files;
      const maxSizeBytes = maxSize * 1024 * 1024;
      
      if (file && file.size > maxSizeBytes) {
        onFileSelect(null, `File size must be less than ${maxSize}MB`);
        return;
      }

      onFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (multiple) {
        handleFileSelect(files);
      } else {
        handleFileSelect(files[0]);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // FIXED: Proper type checking functions
  const isImage = (file) => {
    if (!file) return false;
    
    // Handle File objects
    if (file.type) {
      return file.type.startsWith('image/');
    }
    
    // Handle URL strings
    if (typeof file === 'string') {
      return file.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i);
    }
    
    return false;
  };

  const isPDF = (file) => {
    if (!file) return false;
    
    // Handle File objects
    if (file.type) {
      return file.type === 'application/pdf';
    }
    
    // Handle URL strings
    if (typeof file === 'string') {
      return file.toLowerCase().match(/\.pdf(\?.*)?$/i);
    }
    
    return false;
  };

  const isVideo = (file) => {
    if (!file) return false;
    
    // Handle File objects
    if (file.type) {
      return file.type.startsWith('video/');
    }
    
    // Handle URL strings
    if (typeof file === 'string') {
      return file.toLowerCase().match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i);
    }
    
    return false;
  };

  const getFileName = (file) => {
    if (!file) return '';
    
    // Handle File objects
    if (file.name) {
      return file.name;
    }
    
    return 'Uploaded file';
  };

  const getFileSize = (file) => {
    if (!file || typeof file === 'string') return null;
    return file.size;
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      {currentFile ? (
        <div className="border border-gray-300 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Preview Section */}
              {showPreview && isImage(currentFile) ? (
                <img
                  src={typeof currentFile === 'string' ? currentFile : URL.createObjectURL(currentFile)}
                  alt="Preview"
                  className="w-10 h-10 object-cover rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : showPreview && isVideo(currentFile) ? (
                <video
                  src={typeof currentFile === 'string' ? currentFile : URL.createObjectURL(currentFile)}
                  className="w-10 h-10 object-cover rounded"
                  muted
                />
              ) : (
                <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                  {isPDF(currentFile) ? (
                    <File className="h-5 w-5 text-red-500" />
                  ) : isVideo(currentFile) ? (
                    <div className="h-5 w-5 bg-blue-500 rounded flex items-center justify-center">
                      <span className="text-white text-xs">▶</span>
                    </div>
                  ) : (
                    <File className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              )}
              
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {getFileName(currentFile)}
                </p>
                {getFileSize(currentFile) && (
                  <p className="text-xs text-gray-500">
                    {(getFileSize(currentFile) / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>
            </div>
            
            <button
              type="button"
              onClick={onRemove}
              disabled={uploading}
              className="text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            dragOver 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-sm text-gray-600">Uploading...</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="h-8 w-8 text-gray-400 mx-auto" />
              <div>
                <p className="text-sm text-gray-600">
                  {multiple ? 'Drop files here or ' : 'Drop file here or '}
                  <span className="text-blue-600 font-medium">browse</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Max size: {maxSize}MB{multiple ? ' per file' : ''}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
    </div>
  );
};